import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { Observable, forkJoin, map, of, tap } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { mergeUtilityScheduleLists } from '../utility-schedule-display';
import { Utility, UtilitySchedule, DemandChargeRate, EnergyRate, ScheduleMetadata, UtilityRateScheduleVersion, UtilityScheduleDetail } from '../interfaces/utility.interface';

@Injectable({
  providedIn: 'root'
})
export class UtilityService {
  private readonly http = inject(HttpClient);
  private utilities = signal<Utility[]>([]);
  private schedules = signal<UtilitySchedule[]>([]);
  private demandChargeRates = signal<DemandChargeRate[]>([]);
  private energyRates = signal<EnergyRate[]>([]);
  private lastDemandRatesScheduleKey = signal<string | null>(null);
  private lastEnergyRatesScheduleKey = signal<string | null>(null);
  private baseUrl = `${environment.apiUrl}/api/v1`;

  getUtilities(options?: {
    state?: string | null;
    country?: string | null;
  }): Observable<Utility[]> {
    const params = new URLSearchParams();
    if (options?.state) {
      params.set('state', options.state);
    }
    if (options?.country) {
      params.set('country', options.country);
    }
    const query = params.toString();
    const url = query ? `${this.baseUrl}/utility?${query}` : `${this.baseUrl}/utility`;
    return this.http.get<Utility[]>(url).pipe(
      tap((utilities) => this.utilities.set(utilities))
    );
  }

  getUtilitiesByState(state: string): Observable<Utility[]> {
    return this.getUtilities({ state });
  }

  getUtility(utilityId: number): Observable<Utility> {
    return this.http.get<Utility>(`${this.baseUrl}/utility/${utilityId}`).pipe(
      tap((utility) => {
        // Add the single utility to the existing utilities array
        const currentUtilities = this.utilities();
        const existingIndex = currentUtilities.findIndex(u => u.id === utility.id);
        if (existingIndex >= 0) {
          // Update existing utility
          const updatedUtilities = [...currentUtilities];
          updatedUtilities[existingIndex] = utility;
          this.utilities.set(updatedUtilities);
        } else {
          // Add new utility to the array
          this.utilities.set([...currentUtilities, utility]);
        }
      })
    );
  }

  getUtilitySchedules(
    utilityId: number,
    options?: {
      refreshScheduleList?: boolean;
      includeScheduleIds?: readonly number[];
      /** When false, leaves the shared schedules signal unchanged (e.g. typeahead merge). */
      updateSchedulesSignal?: boolean;
    }
  ): Observable<UtilitySchedule[]> {
    const updateSignal = options?.updateSchedulesSignal !== false;
    return this.fetchUtilitySchedules(utilityId, options).pipe(
      tap((schedules) => {
        if (updateSignal) {
          this.schedules.set(schedules);
        }
      })
    );
  }

  private fetchUtilitySchedules(
    utilityId: number,
    options?: {
      refreshScheduleList?: boolean;
      includeScheduleIds?: readonly number[];
    }
  ): Observable<UtilitySchedule[]> {
    const params = new URLSearchParams();
    if (options?.refreshScheduleList) {
      params.set('refresh_schedule_list', 'true');
    }
    const uniqueIds = [...new Set(options?.includeScheduleIds ?? [])];
    if (uniqueIds.length > 0) {
      params.set('include_schedule_ids', uniqueIds.join(','));
    }
    const query = params.toString() ? `?${params.toString()}` : '';
    const url = `${this.baseUrl}/utility/${utilityId}/utility_schedule${query}`;
    return this.http.get<UtilitySchedule[]>(url);
  }

  /**
   * Loads persisted schedules and fetches any meter-linked rows missing from the list
   * (e.g. archived version rows). Updates the shared schedules signal.
   */
  loadUtilitySchedulesForDisplay(
    utilityId: number,
    requiredScheduleIds: readonly number[] = []
  ): Observable<UtilitySchedule[]> {
    return this.getUtilitySchedules(utilityId, {
      includeScheduleIds: requiredScheduleIds,
      updateSchedulesSignal: true,
    });
  }

  /**
   * Persisted schedules for site display and meter-attached tariffs (may span utilities).
   * Updates the shared schedules signal with a merge that keeps **every version row** (by id).
   * Do not dedupe by external id — billing periods resolve labels via ``utility_schedule_id``.
   */
  loadUtilitySchedulesForDisplayMerged(
    utilityIds: readonly number[],
    requiredScheduleIds: readonly number[] = []
  ): Observable<UtilitySchedule[]> {
    const uniqueIds = [...new Set(utilityIds.filter((id) => id > 0))];
    if (uniqueIds.length === 0) {
      this.schedules.set([]);
      return of([]);
    }
    return forkJoin(
      uniqueIds.map((utilityId) =>
        this.getUtilitySchedules(utilityId, {
          includeScheduleIds: requiredScheduleIds,
          updateSchedulesSignal: false,
        })
      )
    ).pipe(
      map((lists) => mergeUtilityScheduleLists(...lists)),
      tap((schedules) => this.schedules.set(schedules))
    );
  }

  /**
   * One row per logical tariff for schedule typeahead (no billable detail calls).
   *
   * The API merges the provider schedule list with persisted rows and falls back to the
   * database when the provider is unavailable, so this needs no second request.
   */
  getUtilitySchedulesForTypeahead(utilityId: number): Observable<UtilitySchedule[]> {
    return this.http.get<UtilitySchedule[]>(
      `${this.baseUrl}/utility/${utilityId}/utility_schedule/catalog`
    );
  }

  ensureUtilitySchedule(
    utilityId: number,
    externalUtilityScheduleId: string
  ): Observable<UtilitySchedule> {
    return this.http.post<UtilitySchedule>(
      `${this.baseUrl}/utility/${utilityId}/utility_schedule/ensure`,
      { external_utility_schedule_id: externalUtilityScheduleId }
    );
  }

  /**
   * Load schedule + metadata + demand/energy rates in one request (single server sync).
   * Preferred (and only) Angular read path for schedule rates / fixed charges.
   */
  getUtilityScheduleDetail(
    utilityId: number,
    scheduleId: number,
    asOfDate?: string
  ): Observable<UtilityScheduleDetail> {
    const query = asOfDate ? `?as_of_date=${encodeURIComponent(asOfDate)}` : '';
    const scheduleKey = `${utilityId}:${scheduleId}:${asOfDate ?? ''}`;
    return this.http
      .get<UtilityScheduleDetail>(
        `${this.baseUrl}/utility/${utilityId}/utility_schedule/${scheduleId}/detail${query}`
      )
      .pipe(
        tap((detail) => {
          this.demandChargeRates.set(detail.demand_rates ?? []);
          this.energyRates.set(detail.energy_rates ?? []);
          this.lastDemandRatesScheduleKey.set(scheduleKey);
          this.lastEnergyRatesScheduleKey.set(scheduleKey);
        })
      );
  }

  /**
   * Create a new demand rate. Sets manually_entered to true.
   * id and utility_schedule_id are omitted from the request body (API assigns them).
   * Empty string times are sent as null so the API accepts the request.
   */
  createDemandRate(utilityId: number, scheduleId: number, rate: DemandChargeRate): Observable<DemandChargeRate> {
    const { id: _id, utility_schedule_id: _sid, ...rest } = rate;
    const body = { ...rest, manually_entered: true };
    if (body.start_time === '') body.start_time = undefined;
    if (body.end_time === '') body.end_time = undefined;
    return this.http.post<DemandChargeRate>(
      `${this.baseUrl}/utility/${utilityId}/utility_schedule/${scheduleId}/utility_demand_rate`,
      body
    ).pipe(tap((created) => {
      const current = this.demandChargeRates();
      this.demandChargeRates.set([...current, created]);
    }));
  }

  /**
   * Update an existing demand rate. Sets manually_entered to true.
   * Omits id, utility_schedule_id, created, modified from the request body.
   * Empty string times are sent as undefined so the API accepts the request.
   */
  updateDemandRate(utilityId: number, scheduleId: number, rateId: number, rate: Partial<DemandChargeRate>): Observable<DemandChargeRate> {
    const { id: _id, utility_schedule_id: _sid, created: _c, modified: _m, ...rest } = rate as DemandChargeRate;
    const body = { ...rest, manually_entered: true };
    if (body.start_time === '') body.start_time = undefined;
    if (body.end_time === '') body.end_time = undefined;
    return this.http.patch<DemandChargeRate>(
      `${this.baseUrl}/utility/${utilityId}/utility_schedule/${scheduleId}/utility_demand_rate/${rateId}`,
      body
    ).pipe(tap((updated) => {
      const current = this.demandChargeRates();
      const idx = current.findIndex((r) => r.id === rateId);
      if (idx >= 0) {
        const next = current.slice();
        next[idx] = updated;
        this.demandChargeRates.set(next);
      }
    }));
  }

  deleteDemandRate(utilityId: number, scheduleId: number, rateId: number): Observable<void> {
    return this.http.delete<void>(
      `${this.baseUrl}/utility/${utilityId}/utility_schedule/${scheduleId}/utility_demand_rate/${rateId}`
    ).pipe(tap(() => {
      const current = this.demandChargeRates();
      this.demandChargeRates.set(current.filter((r) => r.id !== rateId));
    }));
  }

  hasRatesFetchedForSchedule(utilityId: number, scheduleId: number, asOfDate?: string): boolean {
    const scheduleKey = `${utilityId}:${scheduleId}:${asOfDate ?? ''}`;
    return (
      this.lastDemandRatesScheduleKey() === scheduleKey &&
      this.lastEnergyRatesScheduleKey() === scheduleKey
    );
  }

  getUtilitySchedule(utilityId: number, scheduleId: number): Observable<UtilitySchedule> {
    return this.http.get<UtilitySchedule>(
      `${this.baseUrl}/utility/${utilityId}/utility_schedule/${scheduleId}`
    );
  }

  getUtilityScheduleRateVersions(utilityId: number, scheduleId: number): Observable<UtilityRateScheduleVersion[]> {
    return this.http.get<UtilityRateScheduleVersion[]>(
      `${this.baseUrl}/utility/${utilityId}/utility_schedule/${scheduleId}/rate_versions`
    );
  }

  /**
   * Create a new energy rate. Sets manually_entered to true.
   * id and utility_schedule_id are omitted from the request body (API assigns them).
   * Empty string times are sent as null so the API accepts the request.
   */
  createEnergyRate(utilityId: number, scheduleId: number, rate: EnergyRate): Observable<EnergyRate> {
    const { id: _id, utility_schedule_id: _sid, ...rest } = rate;
    const body = { ...rest, manually_entered: true };
    if (body.start_time === '') body.start_time = undefined;
    if (body.end_time === '') body.end_time = undefined;
    return this.http.post<EnergyRate>(
      `${this.baseUrl}/utility/${utilityId}/utility_schedule/${scheduleId}/utility_energy_rate`,
      body
    ).pipe(tap((created) => {
      const current = this.energyRates();
      this.energyRates.set([...current, created]);
    }));
  }

  /**
   * Update an existing energy rate. Sets manually_entered to true.
   * Omits id, utility_schedule_id, created, modified from the request body.
   * Empty string times are sent as undefined so the API accepts the request.
   */
  updateEnergyRate(utilityId: number, scheduleId: number, rateId: number, rate: Partial<EnergyRate>): Observable<EnergyRate> {
    const { id: _id, utility_schedule_id: _sid, created: _c, modified: _m, ...rest } = rate as EnergyRate;
    const body = { ...rest, manually_entered: true };
    if (body.start_time === '') body.start_time = undefined;
    if (body.end_time === '') body.end_time = undefined;
    return this.http.patch<EnergyRate>(
      `${this.baseUrl}/utility/${utilityId}/utility_schedule/${scheduleId}/utility_energy_rate/${rateId}`,
      body
    ).pipe(tap((updated) => {
      const current = this.energyRates();
      const idx = current.findIndex((r) => r.id === rateId);
      if (idx >= 0) {
        const next = current.slice();
        next[idx] = updated;
        this.energyRates.set(next);
      }
    }));
  }

  deleteEnergyRate(utilityId: number, scheduleId: number, rateId: number): Observable<void> {
    return this.http.delete<void>(
      `${this.baseUrl}/utility/${utilityId}/utility_schedule/${scheduleId}/utility_energy_rate/${rateId}`
    ).pipe(tap(() => {
      const current = this.energyRates();
      this.energyRates.set(current.filter((r) => r.id !== rateId));
    }));
  }

  /**
   * Update schedule metadata (e.g. service charges). Sends PATCH with schedule_metadata and returns the updated metadata.
   */
  patchScheduleMetadata(utilityId: number, scheduleId: number, metadata: ScheduleMetadata): Observable<ScheduleMetadata | null> {
    return this.http.patch<{ schedule_metadata: ScheduleMetadata | null }>(
      `${this.baseUrl}/utility/${utilityId}/utility_schedule/${scheduleId}`,
      { schedule_metadata: metadata }
    ).pipe(
      tap((res) => {
        // Optionally update any cached metadata here if we add a signal for it later
      }),
      map((res) => res.schedule_metadata ?? null)
    );
  }

  // Signal getters
  getUtilitiesSignal() {
    return this.utilities.asReadonly();
  }

  getSchedulesSignal() {
    return this.schedules.asReadonly();
  }

  getDemandChargeRatesSignal() {
    return this.demandChargeRates.asReadonly();
  }

  getEnergyRatesSignal() {
    return this.energyRates.asReadonly();
  }

  // Clear methods for resetting state
  clearUtilities() {
    this.utilities.set([]);
  }

  clearSchedules() {
    this.schedules.set([]);
  }

  clearRates() {
    this.demandChargeRates.set([]);
    this.energyRates.set([]);
    this.lastDemandRatesScheduleKey.set(null);
    this.lastEnergyRatesScheduleKey.set(null);
  }
}
